import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AssetCategory, CreateAssetCategory, UpdateAssetCategory } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['asset-categories', companyId] as const;

export function useAssetCategories(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () =>
      api<AssetCategory[]>(`/companies/${companyId}/asset-categories?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useAssetCategory(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['asset-categories', companyId, id],
    queryFn: () => api<AssetCategory>(`/companies/${companyId}/asset-categories/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateAssetCategory(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateAssetCategory, 'companyId'>) =>
      apiJson<typeof input, AssetCategory>(
        'POST',
        `/companies/${companyId}/asset-categories`,
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdateAssetCategory(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAssetCategory }) =>
      apiJson<UpdateAssetCategory, AssetCategory>(
        'PATCH',
        `/companies/${companyId}/asset-categories/${id}`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteAssetCategory(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, AssetCategory>('DELETE', `/companies/${companyId}/asset-categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
